import React, { useEffect } from 'react';
import { useMaybeRenderedSideDrawerToggle } from '../SideDrawer/MaybeRenderedSideDrawer';
import { getIntl } from '../../../utils/intl';
import getDateString from '../../../utils/getDateString';
import { SideDrawerButtonContainer } from '../Form/SideDrawerFormContainer';
import DayEventList from './DayEventList';
import { CalendarEventHelper } from './useCalendarEvents';
import { usePromptButton } from '../../shared/PromptButton';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import { EventType } from './types';

const intl = getIntl('focusedEventsPrompt', {
    activityOn: 'Activity on {date}',
});

export type FocusedEventsPromptOwnProps = {
    focusedIsoDate: string | null;
    onClearFocusedIsoDate: () => void;
    calendarEventsHelper: CalendarEventHelper;
    profileId: string;
    eventType: EventType;
};

export type FocusedEventsPromptProps = FocusedEventsPromptOwnProps;

const FocusedEventsPrompt: React.FC<FocusedEventsPromptProps> = (props) => {
    const isMobileLayout = useIsMobileLayout();
    const buttonPrompt = usePromptButton({
        promptTitle: intl('activityOn', { date: getDateString(props.focusedIsoDate, 'date') }),
        renderPromptContents: (onClose) =>
            props.focusedIsoDate ? (
                <DayEventList
                    isoDate={props.focusedIsoDate}
                    calendarEventsHelper={props.calendarEventsHelper}
                    profileId={props.profileId}
                    onClosePromptOrSideDrawer={onClose}
                    eventType={props.eventType}
                />
            ) : null,
        onClosed: isMobileLayout ? props.onClearFocusedIsoDate : undefined,
    });

    const sideDrawerToggle = useMaybeRenderedSideDrawerToggle({
        contentKey: 'focusedEventsPrompt',
        title: intl('activityOn', { date: getDateString(props.focusedIsoDate, 'date') }),
        renderContent: (onClose) => (
            <SideDrawerButtonContainer buttons={[]}>
                {props.focusedIsoDate ? (
                    <DayEventList
                        isoDate={props.focusedIsoDate}
                        calendarEventsHelper={props.calendarEventsHelper}
                        profileId={props.profileId}
                        onClosePromptOrSideDrawer={() => onClose(true)}
                        eventType={props.eventType}
                    />
                ) : null}
            </SideDrawerButtonContainer>
        ),
        mobileOnFocus: buttonPrompt.onPress,
        onClosed: isMobileLayout ? undefined : props.onClearFocusedIsoDate,
    });

    useEffect(() => {
        if (props.focusedIsoDate) {
            sideDrawerToggle.onOpen();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.focusedIsoDate]);

    return (
        <>
            {buttonPrompt.Prompt}
            {sideDrawerToggle.SideDrawer}
        </>
    );
};

export default React.memo(FocusedEventsPrompt);
